/**
 * Users API Endpoints
 * Handles user profile data
 */

import { apiClient, getAuthHeaders } from "../client";
import type {
  MessageOrigin,
  RequestOptions,
  SendUserMessageRequest,
  SendUserMessageResponse,
  UserData,
} from "../types";

/**
 * Fetch the authenticated user's profile from the backend
 * @param userId - The Cognito user ID (sub) of the authenticated user
 * @returns User profile data
 */
export async function getUser(
  userId: string,
  options?: RequestOptions,
): Promise<
  { success: true; data: UserData } | { success: false; error: Error }
> {
  try {
    const authHeaders = await getAuthHeaders();
    const result = await apiClient.get<UserData>(
      `/users/${encodeURIComponent(userId)}`,
      {
        ...options,
        headers: {
          ...authHeaders,
          ...options?.headers,
        },
      },
    );

    if (!result.success) {
      const errorMessage =
        result.error.message || "Failed to fetch user profile";
      return {
        success: false,
        error: new Error(errorMessage),
      };
    }

    return {
      success: true,
      data: result.data!,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error("Unknown error"),
    };
  }
}

/**
 * Send a message on behalf of the authenticated user (e.g. a markdown
 * summary of a completed wellbeing check-in) to be relayed to Iris.
 * @param userId - The Cognito user ID (sub) of the authenticated user
 * @param message - Markdown-formatted message content
 * @param messageOrigin - Which channel this message should be treated as
 * having arrived through ("sms" | "imessage" | "rcs")
 */
export async function sendUserMessage(
  userId: string,
  message: string,
  messageOrigin: MessageOrigin,
  options?: RequestOptions,
): Promise<
  | { success: true; data: SendUserMessageResponse }
  | { success: false; error: Error }
> {
  try {
    const authHeaders = await getAuthHeaders();
    const result = await apiClient.post<SendUserMessageResponse>(
      `/users/${encodeURIComponent(userId)}/message`,
      {
        message,
        message_origin: messageOrigin,
      } satisfies SendUserMessageRequest,
      {
        ...options,
        headers: {
          ...authHeaders,
          ...options?.headers,
        },
      },
    );

    if (!result.success) {
      const errorMessage = result.error.message || "Failed to send message";
      return {
        success: false,
        error: new Error(errorMessage),
      };
    }

    return {
      success: true,
      data: result.data!,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error("Unknown error"),
    };
  }
}

export const usersApi = {
  getUser,
  sendUserMessage,
};
